import Flex from "components/Flex";
import { MutableRefObject, useCallback, useContext, useState } from "react";
import { P } from "theme/typography";
import { ClickableIconSX } from "theme";
import { Button, FormControl, InputLabel, MenuItem, Select, Tooltip, FormControlLabel, Switch } from "@mui/material";
import { GetApp, MenuOpen, OpenInNew } from "@mui/icons-material";
import { useAppSelector } from "redux/hooks";
import {
  selectFormFields,
  selectFormPagesVisible,
  selectOutcomeDisabledFormSections,
  selectOutcomeEnabledFormSections,
} from "redux/slices/form.slice";
import ExportModal from "components/ExportModal";
import { CaseInfo, SectionReviewState } from "generated/graphql";
import { selectCase, selectCaseInsightsPending, selectSectionValues } from "redux/slices/case";
import { displayCaseOutcomeStatus, FormFieldValuesUI, parseSafeInt } from "Backend";
import { selectCaseInsightsEnabled, selectLookerSupplementalDataReport } from "redux/slices/clientLens";
import { CaseFormContext } from "contexts/CaseFormContext";
import { AppRoutes } from "AppRoutes";
import { flushSync } from "react-dom";
import { useRunAutoScripts } from "helpers/hooks/useRunAutoScripts";
import useSearchParamsWithUpdate from "helpers/hooks/useSearchParamsWithUpdate";
import { cn } from "components/lib/utils";
import { CaseInsightsRefreshTooltip } from "routes/lenses/lens/case/CaseInsightsRefreshTooltip";
import { SectionsByReviewState, UnreviewableFormSections } from "./SectionsByReviewState";

const SBRCaseOutline = ({
  fieldContainerRef,
  activeSectionId,
  caseId,
  formId,
  caseInfo,
}: {
  formValues: FormFieldValuesUI[];
  fieldContainerRef: MutableRefObject<HTMLElement | null>;
  caseId: number;
  formId: number;
  caseInfo: CaseInfo | undefined;
  activeSectionId: number | undefined;
}) => {
  const [meMode, setMeMode] = useState(false);
  const formSections = useAppSelector(selectOutcomeEnabledFormSections);
  const unreviewableFormSections = useAppSelector(selectOutcomeDisabledFormSections);
  const formPages = useAppSelector(selectFormPagesVisible);
  const sectionValues = useAppSelector(selectSectionValues);
  const caseInsightsPending = useAppSelector(selectCaseInsightsPending);
  const { searchParams, updateParams } = useSearchParamsWithUpdate();
  const paramsFormPageId = parseSafeInt(searchParams.get("formPageId"));
  const firstFormPage = formPages[0];
  const formPageId =
    paramsFormPageId !== undefined ? paramsFormPageId : firstFormPage !== undefined ? firstFormPage.id : undefined;
  const visibleFormSections =
    formPageId === undefined ? formSections : formSections.filter((fs) => fs.formPageId === formPageId);
  const visibleUnreviewableFormSections =
    formPageId === undefined
      ? unreviewableFormSections
      : unreviewableFormSections.filter((fs) => fs.formPageId === formPageId);
  const lensCase = useAppSelector(selectCase);
  const formFields = useAppSelector(selectFormFields);
  const caseForm = useContext(CaseFormContext);
  const lookerSupplementalDataReport = useAppSelector(selectLookerSupplementalDataReport);
  const [open, setOpen] = caseForm.outlineOpenState;
  const [exportModalOpen, setExportModalOpen] = useState<boolean>(false);
  const caseInsightsEnabled = useAppSelector(selectCaseInsightsEnabled);

  const runAutoScripts = useRunAutoScripts(caseId);

  const scrollFieldIntoView = useCallback(
    (fieldId: number) => {
      if (fieldContainerRef.current) {
        const node = fieldContainerRef.current.querySelector(`#form-field-${fieldId}`);
        if (node) {
          node.scrollIntoView();
        }
      }
    },
    [fieldContainerRef.current]
  );

  const onFieldClick = (fieldId: number, sectionId?: number) => {
    flushSync(() => {
      updateParams([
        { key: "formPageId", value: formPageId?.toString() },
        { key: "sectionId", value: sectionId?.toString() },
      ]);
    });
    scrollFieldIntoView(fieldId);
  };

  const exportBody = (
    <Flex>
      <P variant="h6">Profile Export Settings</P>
      {caseInfo && (
        <P>
          {caseInfo.claimantGivenName} {caseInfo.claimantSurname}
        </P>
      )}
    </Flex>
  );

  return (
    <div className={cn("h-full min-w-0 flex flex-col gap-4 py-4 bg-white", open ? "w-[288px]" : "w-[46px]")}>
      <div className="flex items-center">
        <div className={cn("flex w-full items-center", open ? "px-2" : "justify-center")}>
          {open && (
            <div className="flex flex-col shrink-0">
              {lookerSupplementalDataReport && (
                <Button
                  variant="text"
                  size="small"
                  sx={{ px: 0 }}
                  endIcon={<OpenInNew />}
                  href={AppRoutes.supplementalDataURLBuilder(
                    lensCase.lensId,
                    lensCase.id,
                    lookerSupplementalDataReport.id
                  )}
                  target="_blank"
                  rel="noopener noreferrer">
                  Supplemental Data
                </Button>
              )}
            </div>
          )}
          {open && <div className="grow" />}
          <Tooltip title={open ? "Close Overview" : "Open Overview"} arrow>
            <MenuOpen
              sx={{ ...ClickableIconSX, transform: !open ? "rotate(180deg)" : "" }}
              onClick={() => setOpen((cur) => !cur)}
              data-pendo="case-toggle-overview"
            />
          </Tooltip>
        </div>
      </div>
      {open && (
        <div className={cn("flex flex-col gap-2 px-4")}>
          <div className="flex">
            <p className="text-sm font-semibold">Sections by Status</p>
            <div className="grow" />
            <FormControlLabel
              control={<Switch size="small" checked={meMode} onChange={() => setMeMode((prev) => !prev)} />}
              label="Me Mode"
              className="*:text-xxs *:text-gray-400 mr-0"
            />
          </div>
          {lensCase.outcomeStatus !== null && lensCase.outcomeStatus !== undefined && (
            <div className="flex gap-1 text-gray-400 text-xs">
              <p>Overall Outcome:</p>
              <p className="uppercase tracking-wider font-semibold">
                {displayCaseOutcomeStatus(lensCase.outcomeStatus)}
              </p>
            </div>
          )}
        </div>
      )}
      {open && formPages.length > 1 && (
        <div className="px-2">
          <FormControl size="small" variant="standard" fullWidth>
            <InputLabel>Page</InputLabel>
            <Select
              value={formPageId}
              label="Page"
              onChange={(evt) => {
                updateParams([
                  { key: "formPageId", value: evt.target.value.toString() },
                  { key: "sectionId", value: undefined },
                  { key: "fieldId", value: undefined },
                ]);
              }}>
              {formPages.map((fp) => (
                <MenuItem value={fp.id} key={fp.id}>
                  {fp.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      )}
      <div className="px-2">
        <div className="border-b border-gray-50" />
      </div>
      <div className={cn("overflow-y-auto flex flex-col px-2 gap-4 min-h-0", !open ? "px-2" : "")}>
        {visibleFormSections.length > 0 && (
          <>
            <SectionsByReviewState
              activeSectionId={activeSectionId}
              caseId={caseId}
              formId={formId}
              open={open}
              meMode={meMode}
              reviewState={SectionReviewState.Review}
              sectionValues={sectionValues}
              formSections={visibleFormSections}
              formFields={formFields}
              formPageId={formPageId}
              onFieldClick={onFieldClick}
            />
            <SectionsByReviewState
              activeSectionId={activeSectionId}
              caseId={caseId}
              formId={formId}
              open={open}
              meMode={meMode}
              reviewState={SectionReviewState.Validation}
              sectionValues={sectionValues}
              formSections={visibleFormSections}
              formFields={formFields}
              formPageId={formPageId}
              onFieldClick={onFieldClick}
            />
            <SectionsByReviewState
              activeSectionId={activeSectionId}
              caseId={caseId}
              formId={formId}
              open={open}
              meMode={meMode}
              reviewState={SectionReviewState.Complete}
              sectionValues={sectionValues}
              formSections={visibleFormSections}
              formFields={formFields}
              formPageId={formPageId}
              onFieldClick={onFieldClick}
            />
          </>
        )}
        {visibleUnreviewableFormSections.length > 0 && (
          <UnreviewableFormSections
            activeSectionId={activeSectionId}
            formFields={formFields}
            formSections={visibleUnreviewableFormSections}
            formPageId={formPageId}
            open={open}
            onFieldClick={onFieldClick}
          />
        )}
      </div>
      {open && (
        <div className="px-2">
          <Button
            id="case_form_export"
            variant="outlined"
            color="default"
            className="w-full"
            onClick={() => setExportModalOpen(true)}
            startIcon={<GetApp />}>
            Export
          </Button>
          {caseInsightsEnabled && (
            <CaseInsightsRefreshTooltip>
              <Button
                variant="outlined"
                color="default"
                className="w-full mt-4"
                onClick={runAutoScripts}
                disabled={caseInsightsPending}>
                {caseInsightsPending ? "Refreshing Insights..." : "Refresh Case Insights"}
              </Button>
            </CaseInsightsRefreshTooltip>
          )}
        </div>
      )}

      {exportModalOpen && (
        <ExportModal body={exportBody} open={exportModalOpen} setOpen={setExportModalOpen} caseId={caseId} />
      )}
    </div>
  );
};

export default SBRCaseOutline;
